import React, { useState, useEffect, useRef, useCallback } from "react"
import { createPortal } from "react-dom"
import { subscribeToNotifications, markNotificationRead, markAllNotificationsRead } from "../utils/firebaseUtils"

const timeAgo = (timestamp) => {
  if (!timestamp) return ""
  const now = Date.now()
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  const seconds = Math.floor((now - date.getTime()) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export const NotificationBell = ({ currentUser, onNotificationAction }) => {
  const [notifications, setNotifications] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const dropdownRef = useRef(null)
  const bellRef = useRef(null)

  const unreadCount = notifications.filter((n) => !n.read).length

  useEffect(() => {
    if (!currentUser?.uid) return
    const unsubscribe = subscribeToNotifications(currentUser.uid, setNotifications)
    return () => unsubscribe()
  }, [currentUser?.uid])

  const updatePosition = useCallback(() => {
    if (bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect()
      const dropdownWidth = 320
      // Position dropdown below the bell, left-aligned to the bell's left edge
      let left = rect.left
      // Clamp so it doesn't go off-screen left
      if (left < 8) left = 8
      // Clamp so it doesn't go off-screen right
      if (left + dropdownWidth > window.innerWidth - 8) {
        left = window.innerWidth - dropdownWidth - 8
      }
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: left,
        width: dropdownWidth,
        zIndex: 9999,
      })
    }
  }, [])

  const toggleDropdown = useCallback(() => {
    if (!isOpen) {
      updatePosition()
    }
    setIsOpen(prev => !prev)
  }, [isOpen, updatePosition])

  const handleClickOutside = useCallback((e) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target) &&
      bellRef.current &&
      !bellRef.current.contains(e.target)
    ) {
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen, handleClickOutside])

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markNotificationRead(notification.id)
    }
  }

  const handleMarkAllRead = async () => {
    if (currentUser?.uid) {
      await markAllNotificationsRead(currentUser.uid)
    }
  }

  const dropdown = isOpen ? createPortal(
    <div
      ref={dropdownRef}
      className="bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-hidden flex flex-col"
      style={dropdownStyle}
    >
      <div className="p-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800 text-sm">Notifications</h3>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="overflow-y-auto flex-1">
        {notifications.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No notifications</div>
        ) : (
          notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full text-left p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                !notification.read ? "border-l-4 border-l-blue-500 bg-blue-50/30" : ""
              }`}
            >
              <p className="text-sm font-medium text-gray-900">{notification.title}</p>
              <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{notification.message}</p>
              <p className="text-xs text-gray-400 mt-1">{timeAgo(notification.createdAt)}</p>
              {notification.designId && (
                <p
                  className="text-xs text-blue-500 mt-1 hover:text-blue-700 hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!notification.read) {
                      markNotificationRead(notification.id)
                    }
                    if (onNotificationAction) {
                      onNotificationAction(notification)
                    }
                    setIsOpen(false)
                  }}
                >
                  View design →
                </p>
              )}
            </button>
          ))
        )}
      </div>
    </div>,
    document.body
  ) : null

  return (
    <>
      <button
        ref={bellRef}
        onClick={toggleDropdown}
        className="relative p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
        title="Notifications"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {dropdown}
    </>
  )
}

export default NotificationBell
